import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useIntl } from "react-intl";

import { AnalyzeAPI, Ai360UserAPI } from "@ai360/core";
import { DialogBox, DialogBoxFooterType, Loader } from "~/core";
import { IMultiActionList } from "~/core/components/dialog-box/dialog-box";
import MarkdownRenderer from "~/analyze/components/markdownrenderer";
import { getLoginGuid, getTheUserGuid } from "~/login/selectors";
import { actions as notificationActions, MSGTYPE } from "~/notifications";
import { messages } from "~/reports/i18n-messages";
import { logFirebaseEvent } from "~/utils/firebase";

import "./summarize-modal.css";

export interface ReportFileReference {
    FileName: string;
    UserGuid: string;
    ReportGuid: string;
}

interface ParsedSummaryChunks {
    text: string;
    pdf: string | null;
    error: string | null;
}

function parseSummaryChunks(raw: string): ParsedSummaryChunks {
    if (raw.trimStart().startsWith("Error")) {
        return { text: "", pdf: null, error: raw.trim() };
    }

    const lines = raw
        .split(/\n|\}\{/)
        .filter((line) => line.trim() !== "")
        .map((line, i, arr) => {
            const trimmed = line.trim();
            if (i < arr.length - 1 && !trimmed.endsWith("}") && i > 0 && !trimmed.startsWith("{"))
                return "{" + trimmed + "}";
            if (i < arr.length - 1 && !trimmed.endsWith("}")) return trimmed + "}";
            if (i > 0 && !trimmed.startsWith("{")) return "{" + trimmed;
            return trimmed;
        });

    let text = "";
    let pdf: string | null = null;
    let error: string | null = null;

    for (const line of lines) {
        try {
            const parsed = JSON.parse(line);

            switch (parsed.type) {
                case "content_block_delta":
                    if (parsed.delta?.type === "text_delta") {
                        text += parsed.delta.text;
                    }
                    break;
                case "pdf":
                    pdf = parsed.pdf;
                    break;
                case "error":
                    error = parsed.message;
                    break;
            }
        } catch {
            // Partial JSON line, skip and wait for next chunk
        }
    }

    if (text.trimStart().startsWith("Error")) {
        error = text.trim();
        text = "";
    }

    return { text, pdf, error };
}

export interface SummarizeModalProps {
    isOpen: boolean;
    reportFiles: ReportFileReference[];
    numberOfReports: number;
    onClose: () => void;
}

export function SummarizeModal({
    isOpen,
    reportFiles,
    numberOfReports,
    onClose,
}: SummarizeModalProps) {
    const { formatMessage } = useIntl();
    const dispatch = useDispatch();
    const loginGuid = useSelector(getLoginGuid);
    const userGuid = useSelector(getTheUserGuid);

    const { data: creditData } = Ai360UserAPI.useCheckBedrockCreditQuery(
        { loginGuid, userGuid },
        { skip: !loginGuid || !userGuid, refetchOnMountOrArgChange: true }
    );

    const [rawOutput, setRawOutput] = useState("");
    const [textOutput, setTextOutput] = useState("");
    const [pdfOutput, setPdfOutput] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [_isStreaming, setIsStreaming] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const abortControllerRef = useRef<AbortController | null>(null);
    const rawOutputRef = useRef("");
    const prevIsOpenRef = useRef(false);
    const hasLoggedCompletionRef = useRef(false);

    useEffect(() => {
        if (!rawOutput) {
            return;
        }

        const { text, pdf, error } = parseSummaryChunks(rawOutput);

        if (error) {
            setErrorMessage(error);
            setIsLoading(false);
            return;
        }

        setTextOutput(text);
        if (pdf) {
            setPdfOutput(pdf);
        }
        if (text !== "") {
            setIsLoading(false);
        }
    }, [rawOutput]);

    useEffect(() => {
        if (isLoading || hasLoggedCompletionRef.current || errorMessage || !textOutput) {
            return;
        }
        hasLoggedCompletionRef.current = true;
        logFirebaseEvent("summarize_selected_reports", { numberOfReports });
        console.debug(`Summarize Selected Reports ${numberOfReports}`);
    }, [isLoading, errorMessage, textOutput, numberOfReports]);

    useLayoutEffect(() => {
        if (isOpen && !prevIsOpenRef.current) {
            startStreaming();
        }

        if (!isOpen && prevIsOpenRef.current) {
            abortStream();
            resetState();
        }

        prevIsOpenRef.current = isOpen;
    }, [isOpen]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            abortStream();
        };
    }, []);

    const startStreaming = async () => {
        rawOutputRef.current = "";
        setRawOutput("");
        setTextOutput("");
        setPdfOutput(null);
        setErrorMessage(null);
        setIsLoading(true);
        setIsStreaming(true);
        hasLoggedCompletionRef.current = false;

        abortControllerRef.current = new AbortController();

        let fallbackError: string | null = null;

        try {
            await AnalyzeAPI.summarizeReports(
                reportFiles,
                loginGuid ?? "",
                userGuid ?? "",
                (chunk: string) => {
                    rawOutputRef.current += chunk;
                    setRawOutput((prev) => prev + chunk);
                },
                () => setIsStreaming(false)
            );

            const { text, error } = parseSummaryChunks(rawOutputRef.current);
            if (!text && !error) {
                fallbackError = formatMessage(messages.summarizeError);
            }
        } catch (error) {
            if (error?.name === "AbortError") {
                return;
            }
            fallbackError = formatMessage(messages.summarizeError);
        }

        setIsStreaming(false);
        setIsLoading(false);
        if (fallbackError) {
            setErrorMessage(fallbackError);
        }
        if (loginGuid && userGuid) {
            dispatch(
                Ai360UserAPI.endpoints.checkBedrockCredit.initiate(
                    { loginGuid, userGuid },
                    { subscribe: false, forceRefetch: true }
                )
            );
        }
    };

    const abortStream = () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
    };

    const resetState = () => {
        setRawOutput("");
        setTextOutput("");
        setPdfOutput(null);
        setIsLoading(false);
        setIsStreaming(false);
        setErrorMessage(null);
    };

    const handleClose = () => {
        abortStream();
        onClose();
    };

    const handlePrint = () => {
        try {
            if (!pdfOutput) {
                dispatch(
                    notificationActions.pushToasterMessage(
                        messages.summarizePrintError,
                        MSGTYPE.ERROR
                    )
                );
                return;
            }

            const date = new Date()
                .toLocaleDateString("en-US", {
                    month: "2-digit",
                    day: "2-digit",
                    year: "numeric",
                })
                .replace(/\//g, "-");

            const randomNum = String(Math.floor(Math.random() * 1000000)).padStart(6, "0");

            const fileName = `SummarizeReports_${date}_${randomNum}`;

            const linkSource = `data:application/pdf;base64,${pdfOutput}`;
            const downloadLink = document.createElement("a");

            downloadLink.href = linkSource;
            downloadLink.download = fileName;
            downloadLink.click();
        } catch {
            dispatch(
                notificationActions.pushToasterMessage(messages.summarizePrintError, MSGTYPE.ERROR)
            );
        }
    };

    const handleCopy = () => {
        navigator.clipboard.writeText(textOutput).then(
            () => {
                dispatch(
                    notificationActions.pushToasterMessage(
                        formatMessage(messages.summarizeCopySuccess),
                        MSGTYPE.SUCCESS
                    )
                );
            },
            () => {
                dispatch(
                    notificationActions.pushToasterMessage(
                        formatMessage(messages.summarizeCopyError),
                        MSGTYPE.ERROR
                    )
                );
            }
        );
    };

    const getCreditProgressColor = () => {
        if (!creditData) return "green";
        const progress =
            creditData.dailyLimit > 0 ? creditData.currentUsage / creditData.dailyLimit : 0;
        if (!creditData.isAllowed) return "red";
        if (progress >= 0.8) return "orange";
        if (progress >= 0.5) return "yellow";
        return "green";
    };

    const creditFooter = creditData ? (
        <div className="credit-footer">
            <div className="credit-progress-bar">
                <div
                    className={`credit-progress-fill ${getCreditProgressColor()}`}
                    style={{
                        width: `${Math.min(
                            (creditData.currentUsage / creditData.dailyLimit) * 100,
                            100
                        )}%`,
                    }}
                />
            </div>
            <span
                className={`credit-label ${
                    getCreditProgressColor() === "red"
                        ? "red"
                        : getCreditProgressColor() === "orange"
                        ? "orange"
                        : ""
                }`}
            >
                {formatMessage(messages.creditsUsedToday, {
                    current: creditData.currentUsage,
                    limit: creditData.dailyLimit,
                })}
            </span>
        </div>
    ) : null;

    const multiActionList: IMultiActionList[] = [
        {
            action: "Copy",
            actionDisabled: isLoading || !textOutput,
            onAction: handleCopy,
        },
        {
            action: "Print",
            actionDisabled: isLoading || !pdfOutput,
            onAction: handlePrint,
        },
    ];

    return (
        <DialogBox
            isOpen={isOpen}
            onClose={handleClose}
            className="summarize-report-dialog"
            draggable={true}
            unrestricted={true}
            hideCloseX={false}
            footerType={DialogBoxFooterType.MULTI_ACTION}
            multiActionList={multiActionList}
            title={formatMessage(messages.summarizeReportsTitle)}
        >
            <div className="summarize-modal-content">
                <div className={`summarize-loader-wrap${isLoading ? "" : " is-hidden"}`}>
                    <Loader />
                </div>
                <div className={`summarize-output-wrap${isLoading ? " is-hidden" : ""}`}>
                    {textOutput && <MarkdownRenderer markdown={textOutput} />}
                    {errorMessage && <p className="summarize-error-message">{errorMessage}</p>}
                </div>
            </div>
            <p className="summarize-ai-disclaimer">
                {formatMessage(messages.summarizeAiDisclaimer)}
            </p>
            {creditFooter}
        </DialogBox>
    );
}
